import { useQuery } from "@apollo/client/react";
import StackedAreaChart from "components/charts/StackedAreaChart";
import waitForQueries from "components/waitForQueries";
import { GetAllCompaniesWithSites } from "hooks/apollo/company/company.gql";
import EventTypes, { ACTION_OPERATIONS } from "hooks/apollo/usageData/EventTypes";
import useUsageData from "hooks/apollo/usageData/useUsageData";
import fromPairs from "lodash/fromPairs";
import map from "lodash/map";
import uniq from "lodash/uniq";
import { useState } from "react";
import DateTime from "utils/DateTime";
import Enum from "utils/Enum";
import AdminDashboardView from "./AdminDashboardView";

const CHARTS = [
  { eventNames: Object.values(ACTION_OPERATIONS), label: "All usage" },
  { eventNames: [EventTypes.EXPORT_SCHEDULE], label: "Export schedule usage" },
  { eventNames: [EventTypes.FOCUS_LINE_FILTER], label: "Focus line filter" },
  { eventNames: [EventTypes.PUBLISH_SCHEDULE, EventTypes.PUBLISH_WORK_BLOCK], label: "Publish schedule usage" },
  { eventNames: [EventTypes.PUBLISH_WORK_ORDER_DATES], label: "Publish work order dates usage" },
  { eventNames: [EventTypes.RESCHEDULE_MULTIPLE_BLOCKS], label: "Reschedule multiple blocks" },
  { eventNames: [EventTypes.SCHEDULE_TEARDOWN_TIME], label: "Schedule teardown time" },
  { eventNames: [EventTypes.UPDATE_DEFAULT_AVAILABILITIES], label: "Update default availabilities" },
  { eventNames: [EventTypes.UPDATE_AVAILABILITY_OVERRIDES], label: "Update availability overrides" },
];

export default function AdminDashboardContainer() {
  const [siteId, setSiteId] = useState(null);
  const [dateRange, setDateRange] = useState({
    endDate: new Date(),
    startDate: DateTime.timeAgo(30, "days"),
  });

  const handleRangeChange = ({ startDate, endDate }) => {
    setDateRange({ endDate, startDate });
  };

  const handleSiteChange = (selectedSiteId) => {
    setSiteId(selectedSiteId);
  };

  const queries = [useQuery(GetAllCompaniesWithSites)];

  const chartTypes = map(CHARTS, ({ label, eventNames }) => {
    // biome-ignore lint/correctness/useHookAtTopLevel: CHARTS is not going to change length, meaning hooks rules will not break
    const usageData = useUsageData({
      endDate: dateRange.endDate,
      eventNames,
      siteId,
      startDate: dateRange.startDate,
    });

    return {
      ChartComponent: StackedAreaChart,
      data: getRechartCompatibleData(usageData),
      keys: getKeys(usageData),
      label,
    };
  });

  const props = {
    charts: [...chartTypes],
    endDate: dateRange.endDate,
    handleRangeChange,
    handleSiteChange,
    startDate: dateRange.startDate,
  };

  return waitForQueries({ queries })(AdminDashboardView)(props);
}

function getRechartCompatibleData(usageData) {
  if (!usageData) return [];

  return Enum.chain(usageData)
    .groupBy("date")
    .map((rows, date) => ({ name: date, ...getUsageBySite(rows) }))
    .value();
}

function getUsageBySite(rows) {
  return fromPairs(map(rows, (row) => [getSiteKey(row), row.value]));
}

function getKeys(data) {
  if (!data) return [];
  if (data.length === 0) return [];

  return uniq(data.map(getSiteKey));
}

function getSiteKey(row) {
  return `${row.companyName} - ${row.siteName}`;
}
